import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FileText, Megaphone, Send, Trash2 } from "lucide-react";
import { api, errMsg, downloadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";
import { fmtIST } from "@/lib/utils";

export default function Notices() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [form, setForm] = useState({ title: "", body: "", audience: "all", scope: "all", department_id: "", semester_id: "" });
  const [pdf, setPdf] = useState(null);
  const [posting, setPosting] = useState(false);

  const { data: notices = [] } = useQuery({
    queryKey: ["notices"],
    queryFn: async () => (await api.get("/notices")).data,
  });
  const { data: departments = [] } = useQuery({
    queryKey: ["departments"],
    queryFn: async () => (await api.get("/admin/departments")).data,
    enabled: user?.role === "admin",
  });
  const { data: semesters = [] } = useQuery({
    queryKey: ["semesters"],
    queryFn: async () => (await api.get("/admin/semesters")).data,
    enabled: user?.role === "admin" || !!user?.is_hod,
  });
  const { data: mySubjects = [] } = useQuery({
    queryKey: ["teacher-subjects"],
    queryFn: async () => (await api.get("/teacher/subjects")).data,
    enabled: user?.role === "teacher" && !user?.is_hod,
  });
  const semesterOptions = user?.role === "teacher" && !user?.is_hod
    ? [...new Map(mySubjects.map((s) => [s.semester_id, { id: s.semester_id, name: s.semester_name }])).values()]
    : semesters.filter((s) => user?.role === "admin" || s.department_id === user?.department_id);

  const canPost = user?.role === "teacher" || user?.role === "admin";

  const submit = async (e) => {
    e.preventDefault();
    setPosting(true);
    try {
      const fd = new FormData();
      fd.append("title", form.title);
      fd.append("body", form.body);
      fd.append("audience", form.audience);
      fd.append("scope", form.scope);
      if (form.scope === "department" && form.department_id) fd.append("department_id", form.department_id);
      if (form.scope === "semester" && form.semester_id) fd.append("semester_id", form.semester_id);
      if (pdf) fd.append("pdf", pdf);
      await api.post("/notices", fd);
      toast.success("Notice posted");
      setForm({ title: "", body: "", audience: "all", scope: "all", department_id: "", semester_id: "" });
      setPdf(null);
      queryClient.invalidateQueries({ queryKey: ["notices"] });
    } catch (e2) {
      toast.error(errMsg(e2));
    } finally {
      setPosting(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/notices/${id}`);
      toast.success("Notice deleted");
      queryClient.invalidateQueries({ queryKey: ["notices"] });
    } catch (e2) {
      toast.error(errMsg(e2));
    }
  };

  return (
    <Layout title="Notices">
      {canPost && (
        <div className="bg-white border border-slate-200 rounded-md p-6 mb-8" data-testid="notice-form-card">
          <h2 className="font-heading text-lg font-semibold tracking-tight text-slate-950 mb-4">Post a notice</h2>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label>Title</Label>
              <Input data-testid="notice-title-input" required value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Notice</Label>
              <Textarea data-testid="notice-body-input" required rows={4} value={form.body}
                onChange={(e) => setForm({ ...form, body: e.target.value })} />
            </div>
            {(user?.role === "admin" || user?.role === "teacher") && (
              <div className="space-y-1.5">
                <Label>Addressed to</Label>
                <Select value={form.audience} onValueChange={(v) => setForm({ ...form, audience: v })}>
                  <SelectTrigger data-testid="notice-audience-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all" data-testid="audience-all">Everyone</SelectItem>
                    <SelectItem value="teachers" data-testid="audience-teachers">Teachers only</SelectItem>
                    <SelectItem value="students" data-testid="audience-students">Students only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            {(user?.role === "admin" || user?.role === "teacher") && (
              <div className="space-y-1.5">
                <Label>Scope</Label>
                <Select value={form.scope} onValueChange={(v) => setForm({ ...form, scope: v, semester_id: "" })}>
                  <SelectTrigger data-testid="notice-scope-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all" data-testid="scope-all">All departments</SelectItem>
                    <SelectItem value="department" data-testid="scope-department">{user?.role === "admin" ? "Specific department" : "My department only"}</SelectItem>
                    <SelectItem value="semester" data-testid="scope-semester">Specific semester</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            {form.scope === "department" && user?.role === "admin" && (
              <div className="space-y-1.5">
                <Label>Department</Label>
                <Select value={form.department_id} onValueChange={(v) => setForm({ ...form, department_id: v })}>
                  <SelectTrigger data-testid="notice-dept-select"><SelectValue placeholder="Select department" /></SelectTrigger>
                  <SelectContent>
                    {departments.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {form.scope === "semester" && (
              <div className="space-y-1.5">
                <Label>Semester</Label>
                <Select value={form.semester_id} onValueChange={(v) => setForm({ ...form, semester_id: v })}>
                  <SelectTrigger data-testid="notice-semester-select"><SelectValue placeholder="Select semester" /></SelectTrigger>
                  <SelectContent>
                    {semesterOptions.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1.5">
              <Label>PDF attachment (optional)</Label>
              <Input type="file" accept="application/pdf" data-testid="notice-pdf-input"
                onChange={(e) => setPdf(e.target.files?.[0] || null)} />
            </div>
            <Button type="submit" disabled={posting} data-testid="notice-submit-btn"
              className="bg-slate-950 hover:bg-slate-800 text-white">
              <Send className="h-4 w-4" /> {posting ? "Posting…" : "Post notice"}
            </Button>
          </form>
        </div>
      )}

      <div className="space-y-4" data-testid="notices-list">
        {notices.length === 0 && (
          <div className="border border-dashed border-slate-300 rounded-md p-16 text-center bg-white" data-testid="notices-empty">
            <Megaphone className="h-8 w-8 text-slate-300 mx-auto mb-3" strokeWidth={1.5} />
            <p className="text-slate-500">No notices yet.</p>
          </div>
        )}
        {notices.map((n) => (
          <div key={n.id} className="bg-white border border-slate-200 rounded-md p-6 transition-transform duration-200 hover:-translate-y-0.5 hover:shadow-sm"
            data-testid={`notice-card-${n.id}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <h3 className="font-heading text-lg font-semibold tracking-tight text-slate-950">{n.title}</h3>
                <p className="text-xs text-slate-400 mt-1 font-data">
                  {n.created_by_name} · {fmtIST(n.created_at)}
                </p>
                {n.audience && n.audience !== "all" && (
                  <Badge variant="outline" className="mt-2 bg-blue-50 text-[#002FA7] border-blue-200" data-testid={`notice-audience-${n.id}`}>
                    {n.audience === "teachers" ? "Teachers only" : "Students only"}
                  </Badge>
                )}
                {n.scope && n.scope !== "all" && (
                  <Badge variant="outline" className="mt-2 ml-2 bg-slate-100 text-slate-600 border-slate-200" data-testid={`notice-scope-${n.id}`}>
                    {n.scope === "department" ? "Department" : "Semester"}
                  </Badge>
                )}
              </div>
              {(user?.role === "admin" || n.created_by === user?.id) && (
                <Button variant="ghost" size="icon" data-testid={`delete-notice-${n.id}`} onClick={() => remove(n.id)}
                  className="text-slate-400 hover:text-red-600 hover:bg-red-50 shrink-0">
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
            <p className="text-sm text-slate-700 mt-3 whitespace-pre-wrap">{n.body}</p>
            {n.file_id && (
              <Button variant="outline" size="sm" className="mt-4" data-testid={`notice-pdf-${n.id}`}
                onClick={() => downloadFile(`/notices/${n.id}/file`, n.pdf_filename || "notice.pdf")}>
                <FileText className="h-4 w-4" /> {n.pdf_filename || "Download PDF"}
              </Button>
            )}
          </div>
        ))}
      </div>
    </Layout>
  );
}
